"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const DISMISS_STORAGE_KEY = "jorvioquotes:deployment-prompt-dismissed-for";

function readDismissedFor(): string | null {
  try {
    return sessionStorage.getItem(DISMISS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDismissedFor(buildId: string) {
  try {
    sessionStorage.setItem(DISMISS_STORAGE_KEY, buildId);
  } catch {
    /* ignore */
  }
}

type VersionResponse = { buildId?: string };

export function DeploymentUpdateNotifier({ initialBuildId }: { initialBuildId: string }) {
  const [visible, setVisible] = useState(false);
  const [remoteBuildId, setRemoteBuildId] = useState<string | null>(null);
  const initialRef = useRef(initialBuildId);

  useEffect(() => {
    initialRef.current = initialBuildId;
  }, [initialBuildId]);

  const evaluate = useCallback((remote: string | undefined) => {
    if (!remote || remote === "local" || initialRef.current === "local") {
      setVisible(false);
      setRemoteBuildId(null);
      return;
    }
    if (remote === initialRef.current) {
      setVisible(false);
      setRemoteBuildId(null);
      return;
    }
    if (readDismissedFor() === remote) {
      setVisible(false);
      setRemoteBuildId(null);
      return;
    }
    setRemoteBuildId(remote);
    setVisible(true);
  }, []);

  const fetchVersion = useCallback(async () => {
    try {
      const r = await fetch("/api/version", { cache: "no-store" });
      const j = (await r.json().catch(() => ({}))) as VersionResponse;
      evaluate(typeof j.buildId === "string" ? j.buildId : undefined);
    } catch {
      /* offline or transient */
    }
  }, [evaluate]);

  useEffect(() => {
    void fetchVersion();
    const interval = window.setInterval(() => void fetchVersion(), 60_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void fetchVersion();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", fetchVersion);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", fetchVersion);
    };
  }, [fetchVersion]);

  if (!visible || !remoteBuildId) return null;

  return (
    <div
      role="status"
      className={cn(
        "fixed bottom-4 left-4 right-4 z-[110] mx-auto flex max-w-md flex-col gap-3 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-lg sm:left-auto sm:right-4 sm:mx-0"
      )}
    >
      <div className="flex gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
          aria-hidden
        >
          <Info className="h-4 w-4" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1 space-y-1 pt-0.5">
          <p className="text-sm font-semibold leading-snug text-foreground">A new version of this page is available</p>
          <p className="text-sm leading-relaxed text-muted-foreground">Refresh to see the latest changes.</p>
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-primary/35 bg-background text-primary hover:bg-primary/10 hover:text-primary"
          onClick={() => {
            writeDismissedFor(remoteBuildId);
            setVisible(false);
          }}
        >
          Not now
        </Button>
        <Button
          type="button"
          variant="default"
          size="sm"
          className="bg-primary text-primary-foreground shadow-soft hover:bg-primary/90"
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      </div>
    </div>
  );
}
